const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '2:00 PM', '3:00 PM', '4:00 PM'];
const demoTypes = [
  { id: 'general', label: 'General Overview', desc: '30 min walkthrough of all automation services', icon: 'ri-presentation-line' },
  { id: 'chatbot', label: 'AI Chatbot Demo', desc: 'See how our chatbot captures leads live', icon: 'ri-robot-2-line' },
  { id: 'crm', label: 'CRM Automation', desc: 'Full CRM pipeline and automation demo', icon: 'ri-contacts-book-2-line' },
  { id: 'custom', label: 'Custom Consultation', desc: 'Tailored session for your specific business needs', icon: 'ri-service-line' },
];

const BOOKING_FORM_URL = 'https://readdy.ai/api/form/d6tu8bf5ehaffuo432tg';
const DEMO_BOOKING_HANDLER_URL = 'https://rvajmknywjpopbjfjagt.supabase.co/functions/v1/demo-booking-handler';

import { useState, FormEvent } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

const BookingSection = () => {
  const { user, profile } = useAuth();
  const [selectedType, setSelectedType] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const displayName = profile
    ? `${profile.first_name} ${profile.last_name}`
    : user?.user_metadata?.first_name
    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
    : '';

  const selectedDemoLabel = demoTypes.find((d) => d.id === selectedType)?.label || '';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedType || !selectedDate || !selectedSlot) return;
    setSubmitting(true);
    setError(false);
    try {
      const body = new URLSearchParams({
        name: displayName || user?.email || 'Member',
        email: user?.email || '',
        demo_type: selectedDemoLabel,
        date: selectedDate,
        time_slot: selectedSlot,
        message: message,
      });
      const res = await fetch(BOOKING_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        // Trigger automated email sequence
        fetch(DEMO_BOOKING_HANDLER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: displayName || user?.email || 'Member',
            email: user?.email || '',
            demo_type: selectedDemoLabel,
            preferred_date: selectedDate,
            preferred_time: selectedSlot,
            message: message,
            source: 'dashboard',
          }),
        }).catch(() => {/* non-blocking */});

        setSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/20 mb-6">
          <i className="ri-calendar-check-2-line text-green-400 text-4xl"></i>
        </div>
        <h2 className="text-white font-bold text-2xl mb-3">Demo Request Sent!</h2>
        <p className="text-ea-gray text-sm max-w-md mb-6">
          Thanks! We&apos;ve received your demo request for <span className="text-white font-medium">{selectedDate} at {selectedSlot}</span>.
          Our team will confirm your booking shortly at <span className="text-ea-blue">{user?.email}</span>.
        </p>
        <button
          onClick={() => { setSubmitted(false); setSelectedType(''); setSelectedSlot(''); setSelectedDate(''); setMessage(''); }}
          className="px-6 py-2.5 border border-ea-blue/40 text-ea-blue text-sm rounded-lg hover:bg-ea-blue/10 transition-colors cursor-pointer whitespace-nowrap"
        >
          Book Another
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-white font-bold text-xl">Book a Demo</h2>
        <p className="text-ea-gray text-sm mt-0.5">Schedule a personalised session with our automation experts</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <form
          data-readdy-form
          id="dashboard-booking-form"
          onSubmit={handleSubmit}
          className="lg:col-span-3 space-y-6"
        >
          {/* Demo type */}
          <div>
            <p className="text-white text-sm font-semibold mb-3">1. Choose Demo Type</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {demoTypes.map((type) => (
                <button
                  type="button"
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    selectedType === type.id
                      ? 'border-ea-blue bg-ea-blue/10'
                      : 'border-ea-dark-border hover:border-ea-blue/40 bg-ea-dark-card'
                  }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${selectedType === type.id ? 'bg-ea-blue/20 border border-ea-blue/30' : 'bg-ea-black border border-ea-dark-border'}`}>
                    <i className={`${type.icon} ${selectedType === type.id ? 'text-ea-blue' : 'text-ea-gray'} text-sm`}></i>
                  </div>
                  <div>
                    <p className={`text-xs font-semibold mb-0.5 ${selectedType === type.id ? 'text-white' : 'text-white/80'}`}>{type.label}</p>
                    <p className="text-ea-gray/60 text-[10px] leading-relaxed">{type.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <p className="text-white text-sm font-semibold mb-3">2. Select Date</p>
            <input
              type="date"
              name="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full bg-ea-dark-card border border-ea-dark-border rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-ea-blue transition-colors cursor-pointer"
            />
          </div>

          {/* Time slots */}
          <div>
            <p className="text-white text-sm font-semibold mb-3">3. Pick a Time (AEST)</p>
            <div className="grid grid-cols-3 gap-2.5">
              {timeSlots.map((slot) => (
                <button
                  type="button"
                  key={slot}
                  onClick={() => setSelectedSlot(slot)}
                  className={`py-2.5 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer border ${
                    selectedSlot === slot
                      ? 'bg-ea-blue border-ea-blue text-white'
                      : 'bg-ea-dark-card border-ea-dark-border text-ea-gray hover:border-ea-blue/40 hover:text-white'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <p className="text-white text-sm font-semibold mb-3">4. Add a Note (Optional)</p>
            <textarea
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 500))}
              placeholder="Tell us about your business and what you&apos;d like to automate..."
              rows={3}
              className="w-full bg-ea-dark-card border border-ea-dark-border rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue transition-colors resize-none"
            />
            <p className="text-ea-gray/40 text-[10px] mt-1 text-right">{message.length}/500</p>
          </div>

          {error && (
            <p className="text-red-400 text-xs">Something went wrong. Please try again or email us at sales@everythingautomation.co</p>
          )}

          <button
            type="submit"
            disabled={!selectedType || !selectedDate || !selectedSlot || submitting}
            className="w-full py-3.5 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            style={{ boxShadow: '0 0 20px rgba(14,165,233,0.2)' }}
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin"></i> Sending Request...</>
            ) : (
              <><i className="ri-calendar-2-line"></i> Confirm Booking</>
            )}
          </button>
        </form>

        {/* Info panel */}
        <div className="lg:col-span-2 space-y-4">
          {displayName && (
            <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ea-blue to-ea-blue-electric flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{displayName}</p>
                  <p className="text-ea-gray/60 text-xs">{user?.email}</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10 border border-green-500/20">
                <i className="ri-verified-badge-line text-green-400 text-xs"></i>
                <span className="text-green-400 text-[10px] font-medium">Verified Member</span>
              </div>
            </div>
          )}
          <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-5 space-y-4">
            <h3 className="text-white font-semibold text-sm">What to Expect</h3>
            {[
              { icon: 'ri-time-line', text: '30-minute personalised session' },
              { icon: 'ri-vidicon-line', text: 'Live video call with our team' },
              { icon: 'ri-lightbulb-flash-line', text: 'Custom automation strategy for your business' },
              { icon: 'ri-file-list-3-line', text: 'Free written action plan after the call' },
              { icon: 'ri-shield-check-line', text: 'No hard sell, just genuine advice' },
            ].map((item) => (
              <div key={item.text} className="flex items-start gap-3">
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${item.icon} text-ea-blue text-sm`}></i>
                </div>
                <p className="text-ea-gray text-xs">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-mail-send-line text-ea-blue text-sm"></i>
              <p className="text-white text-xs font-semibold">Confirmations sent to</p>
            </div>
            <p className="text-ea-gray/60 text-xs">sales@everythingautomation.co</p>
            <p className="text-ea-gray/40 text-[10px] mt-1">We&apos;ll reply within 2 hours to confirm your session.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSection;
